import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button, Box, Typography, Paper, FormHelperText } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled component for the signature pad container
const SignatureContainer = styled(Paper)(({ theme }) => ({
  width: '100%',
  margin: '20px 0',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  position: 'relative',
  touchAction: 'none', // Prevents scrolling on touch devices when signing
}));

// Styled component for the signature pad
const SignaturePad = styled(Box)({
  width: '100%',
  height: '200px',
  '& canvas': {
    width: '100%',
    height: '100%',
  },
});

const Signature = forwardRef(({ 
  onSignatureChange, 
  label = 'Signature', 
  required = false,
  onValidationChange = null,
  showValidationErrors = false
}, ref) => {
  const sigCanvas = useRef({});
  const [isSigned, setIsSigned] = useState(false);
  const [validationError, setValidationError] = useState('');
  // Expose validation method via ref
  useImperativeHandle(ref, () => ({
    validate: () => validate(),
    isValid: () => {
      const error = validate(false);
      return error === null;
    }
  }));
  // Validation function
  const validate = useCallback((setErrors = true) => {
    if (required && !isSigned) {
      const error = `Please provide your signature for ${label || 'the form'}`;
      if (setErrors && showValidationErrors) {
        setValidationError(error);
      }
      return error;
    }
    
    if (setErrors && showValidationErrors) {
      setValidationError('');
    }
    return null;
  }, [required, isSigned, label, showValidationErrors]);
  // Update validation state when signature changes or showValidationErrors changes
  useEffect(() => {
    if (onValidationChange) {
      const isValid = validate(false) === null;
      onValidationChange(isValid);
    }
    
    // Only update the error message if we're showing validation errors
    if (showValidationErrors) {
      validate();
    }
  }, [isSigned, showValidationErrors, onValidationChange, validate]);

  // Check if signature pad has been signed
  const handleEndStroke = () => {
    setIsSigned(!sigCanvas.current.isEmpty());
    save();
  };

  // Clear signature pad
  const clear = () => {
    sigCanvas.current.clear();
    setIsSigned(false);
    if (onSignatureChange) {
      onSignatureChange(null);
    }
  };

  // Save signature as base64 data URL
  const save = () => {
    if (sigCanvas.current.isEmpty()) {
      return null;
    }
    
    const dataURL = sigCanvas.current.toDataURL('image/png');
    if (onSignatureChange) {
      onSignatureChange(dataURL);
    }
    return dataURL;
  };

  // Handle window resize to maintain proper signature aspect ratio
  useEffect(() => {
    const handleResize = () => {
      if (sigCanvas.current && sigCanvas.current.getCanvas) {
        try {
          // Get the actual canvas element from the SignatureCanvas component
          const canvasElement = sigCanvas.current.getCanvas();
          
          if (canvasElement) {
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            canvasElement.width = canvasElement.offsetWidth * ratio;
            canvasElement.height = canvasElement.offsetHeight * ratio;
            
            const ctx = canvasElement.getContext("2d");
            if (ctx) {
              ctx.scale(ratio, ratio);
            }
            
            // If there's content, redraw it to prevent it from being cleared on resize
            if (!sigCanvas.current.isEmpty()) {
              const tempData = sigCanvas.current.toData();
              sigCanvas.current.clear();
              sigCanvas.current.fromData(tempData);
            }
          }
        } catch (err) {
          console.error("Error resizing signature canvas:", err);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Box>
      <Typography 
        variant="subtitle1" 
        gutterBottom
        sx={{ 
          display: 'flex', 
          alignItems: 'center' 
        }}
      >
        {label} {required && <Typography color="error" component="span" ml={0.5}>*</Typography>}
      </Typography>
      
      <SignatureContainer elevation={0}>
        <SignaturePad>
          <SignatureCanvas
            ref={sigCanvas}
            penColor="black"
            canvasProps={{
              className: 'signature-canvas',
              style: { touchAction: 'none' }
            }}
            onEnd={handleEndStroke}
          />
        </SignaturePad>
      </SignatureContainer>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
        <Button 
          variant="outlined" 
          color="secondary"
          onClick={clear}
          disabled={!isSigned}
        >
          Clear
        </Button>
      </Box>
      
      {validationError && (
        <FormHelperText error>{validationError}</FormHelperText>
      )}
    </Box>
  );
});

export default Signature;